import {
    type GoogleProtobufTimestamp,
    type Maybe,
    MetricField,
    type MyLensesOrganicStatsResponseEntry,
    type OrganicStatsResponseDimensionEntry,
} from "../../gql-generated/types";
import { Scale } from "../../lens-stats/formatters";
import { timestampToDate } from "../../shared/utils/date";
import { convertChartLabelDate, dateFormatKey, dateFormatRich } from "../converters";
import { extractMetricFromList } from "../filters";
import { generateDefaultMetrics } from "../generators";
import { roundWithScale, safeDivide } from "../math";
import { ComputedMetricField, type InsightsMetricsMap, type StatsItem } from "../models";

const computeSimpleMetric = (
    numerator: OrganicStatsResponseDimensionEntry,
    denominator: OrganicStatsResponseDimensionEntry,
    timestamp: Maybe<GoogleProtobufTimestamp> | undefined,
    isDateIncomplete?: boolean,
    isPercentage?: boolean,
): StatsItem => {
    const [numeratorValue, denominatorValue] = [numerator, denominator].map((dimensionStats) =>
        Number.parseInt(dimensionStats.statsList[0]?.value ?? ""),
    );

    const quotient = safeDivide(numeratorValue ?? 0, denominatorValue ?? 0) * (isPercentage ? 100 : 1);
    const value = roundWithScale(Scale.h)(quotient);

    const date = timestampToDate(timestamp);
    return {
        value,
        key: convertChartLabelDate(date, dateFormatKey),
        date: `${convertChartLabelDate(date, dateFormatRich)} UTC`,
        isDateIncomplete,
    };
};

export const mapInsightsComputedMetrics = (
    responseEntryList: readonly MyLensesOrganicStatsResponseEntry[],
    containsIncompleteDate?: boolean,
): InsightsMetricsMap<StatsItem> => {
    const computedMetrics = generateDefaultMetrics([
        ComputedMetricField.LensCTR,
        ComputedMetricField.AverageLensFaceFound,
        ComputedMetricField.AverageLensFaceLost,
        ComputedMetricField.AverageLensObjectTracked,
        ComputedMetricField.AverageLensObjectTracked_3D,
        ComputedMetricField.AverageLensMarkerTracked,
        ComputedMetricField.AverageLensTap,
    ]);

    const [responseEntry] = responseEntryList;

    if (!responseEntry) {
        return computedMetrics;
    }

    const entries = responseEntry.statsResponseTimeEntryList;
    const entriesCurr = entries.slice(entries.length / 2);

    for (let i = 0; i < entriesCurr.length; i++) {
        const entry = entriesCurr[i];
        if (!entry) {
            continue;
        }

        const { statsList: stats, timestamp } = entry;

        const isDateIncomplete = containsIncompleteDate && i === entriesCurr.length - 1;
        // computed data
        const [
            swipeUps,
            lensPlays,
            lensFaceFound,
            lensFaceLost,
            lensObjectTracked,
            lensObjectTracked3d,
            lensMarkerTracked,
            lensTap,
        ] = [
            MetricField.SwipeUps,
            MetricField.LensPlays,
            MetricField.LensFaceFound,
            MetricField.LensFaceLost,
            MetricField.LensObjectTracked,
            MetricField.LensObjectTracked_3D,
            MetricField.LensMarkerTracked,
            MetricField.LensTap,
        ].map((name) => extractMetricFromList(stats, name));

        if (lensPlays && swipeUps && computedMetrics.LENS_CTR) {
            computedMetrics.LENS_CTR.stats = [
                ...computedMetrics.LENS_CTR.stats,
                computeSimpleMetric(swipeUps, lensPlays, timestamp, isDateIncomplete, true),
            ];
        }
        if (lensPlays && lensFaceFound && computedMetrics.AVERAGE_LENS_FACE_FOUND) {
            computedMetrics.AVERAGE_LENS_FACE_FOUND.stats = [
                ...computedMetrics.AVERAGE_LENS_FACE_FOUND.stats,
                computeSimpleMetric(lensFaceFound, lensPlays, timestamp, isDateIncomplete),
            ];
        }
        if (lensPlays && lensFaceLost && computedMetrics.AVERAGE_LENS_FACE_LOST) {
            computedMetrics.AVERAGE_LENS_FACE_LOST.stats = [
                ...computedMetrics.AVERAGE_LENS_FACE_LOST.stats,
                computeSimpleMetric(lensFaceLost, lensPlays, timestamp, isDateIncomplete),
            ];
        }
        if (lensPlays && lensObjectTracked && computedMetrics.AVERAGE_LENS_OBJECT_TRACKED) {
            computedMetrics.AVERAGE_LENS_OBJECT_TRACKED.stats = [
                ...computedMetrics.AVERAGE_LENS_OBJECT_TRACKED.stats,
                computeSimpleMetric(lensObjectTracked, lensPlays, timestamp, isDateIncomplete),
            ];
        }
        if (lensPlays && lensObjectTracked3d && computedMetrics.AVERAGE_LENS_OBJECT_TRACKED_3D) {
            computedMetrics.AVERAGE_LENS_OBJECT_TRACKED_3D.stats = [
                ...computedMetrics.AVERAGE_LENS_OBJECT_TRACKED_3D.stats,
                computeSimpleMetric(lensObjectTracked3d, lensPlays, timestamp, isDateIncomplete),
            ];
        }
        if (lensPlays && lensMarkerTracked && computedMetrics.AVERAGE_LENS_MARKER_TRACKED) {
            computedMetrics.AVERAGE_LENS_MARKER_TRACKED.stats = [
                ...computedMetrics.AVERAGE_LENS_MARKER_TRACKED.stats,
                computeSimpleMetric(lensMarkerTracked, lensPlays, timestamp, isDateIncomplete),
            ];
        }
        if (lensPlays && lensTap && computedMetrics.AVERAGE_LENS_TAP) {
            computedMetrics.AVERAGE_LENS_TAP.stats = [
                ...computedMetrics.AVERAGE_LENS_TAP.stats,
                computeSimpleMetric(lensTap, lensPlays, timestamp, isDateIncomplete),
            ];
        }
    }

    return computedMetrics;
};
